import { useEffect } from "react";
import { Link, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Bell, FileText, LogOut, Pill as PillIcon, RefreshCw, User, Users } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Loading, StateBlock } from "@/components/common";
import { BrandLockup } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { isNativeApp } from "@/lib/nativeBridge";
import { enablePush, onNativeToken, onNotificationOpen, pushStatus } from "@/lib/notificationsApi";
import { PatientProvider, useSelectedPatient } from "./patientContext";

const TABS = [
  { to: "/apoio", label: "Paciente", icon: User, testId: "apoio-tab-paciente" },
  { to: "/apoio/boletins", label: "Boletins", icon: FileText, testId: "apoio-tab-boletins", badge: "unread_bulletins" },
  {
    to: "/apoio/medicamentos",
    label: "Medicamentos",
    icon: PillIcon,
    testId: "apoio-tab-medicamentos",
    badge: "unread_medication_events",
  },
  { to: "/apoio/alertas", label: "Alertas", icon: Bell, testId: "apoio-tab-alertas" },
];

const BottomTabs = () => {
  const location = useLocation();
  const { selectedPatient } = useSelectedPatient();

  return (
    <nav
      data-testid="apoio-bottom-nav"
      aria-label="Navegação principal"
      className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-white/95 backdrop-blur-md"
    >
      <div className="mx-auto flex max-w-md items-stretch justify-around gap-1 px-3 pb-6 pt-3">
        {TABS.map((tab) => {
          const active = location.pathname === tab.to;
          const unread = tab.badge ? selectedPatient?.[tab.badge] || 0 : 0;
          return (
            <Link
              key={tab.to}
              to={tab.to}
              data-testid={tab.testId}
              aria-label={unread ? `${tab.label} — ${unread} não lidos` : tab.label}
              aria-current={active ? "page" : undefined}
              className={`relative flex min-h-[56px] flex-1 flex-col items-center justify-center gap-1 rounded-2xl px-1 text-[11.5px] font-semibold transition-colors ${
                active ? "bg-sage-50 text-sage-700 ring-1 ring-sage-600/20" : "text-ink-500 hover:text-sage-600"
              }`}
            >
              <tab.icon size={21} aria-hidden="true" strokeWidth={active ? 2.2 : 1.8} />
              {tab.label}
              {unread ? (
                <span
                  data-testid={`${tab.testId}-badge`}
                  className="absolute right-1.5 top-1 grid h-[18px] min-w-[18px] place-items-center rounded-full bg-terracotta-500 px-1 text-[10.5px] font-bold text-white ring-2 ring-white"
                >
                  {unread}
                </span>
              ) : null}
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

const NoPatients = () => {
  const { loadPatients, signOut, error } = useSelectedPatient();
  return (
    <div className="mx-auto max-w-md px-6 py-16">
      <StateBlock
        testId="apoio-no-patients"
        icon={Users}
        title="Nenhum paciente disponível"
        description={
          error ||
          "Não há vínculo de acompanhamento ativo para a sua conta. Se o seu acesso foi encerrado, fale com a coordenação clínica."
        }
      />
      <div className="mt-8 flex flex-col gap-3">
        <Button
          data-testid="apoio-no-patients-refresh"
          onClick={() => loadPatients()}
          className="h-12 rounded-full bg-sage-600 text-bone hover:bg-sage-700"
        >
          <RefreshCw size={16} className="mr-2" /> Atualizar
        </Button>
        <Button
          data-testid="apoio-no-patients-logout"
          variant="outline"
          onClick={signOut}
          className="h-12 rounded-full border-sage-600/40 text-sage-700"
        >
          <LogOut size={16} className="mr-2" /> Sair
        </Button>
      </div>
    </div>
  );
};

const ChoosePatient = () => {
  const { patients, selectPatient } = useSelectedPatient();
  return (
    <div data-testid="apoio-choose-patient" className="mx-auto max-w-md px-6 py-12">
      <p className="er-label">Acompanhamento autorizado</p>
      <h1 className="mt-3 font-display text-3xl font-medium tracking-tight text-ink-900">Selecione o paciente</h1>
      <p className="mt-3 text-sm leading-relaxed text-ink-500">
        Você acompanha mais de uma pessoa. Escolha com quem deseja continuar; é possível trocar depois na aba Paciente.
      </p>
      <div className="mt-8 space-y-3">
        {patients.map((p, i) => (
          <button
            key={p.patient_id}
            type="button"
            data-testid={`apoio-choose-patient-${p.patient_id}`}
            onClick={() => selectPatient(p.patient_id)}
            className="er-card er-rise w-full text-left transition-colors hover:border-sage-600/40"
            style={{ animationDelay: `${i * 70}ms` }}
          >
            <span className="block font-display text-lg font-medium text-ink-900">{p.patient_name}</span>
            <span className="mt-1 block text-sm text-ink-500">{p.relationship}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

const ApoioShell = () => {
  const { user } = useAuth();
  const { patients, selectedPatientId, signOut } = useSelectedPatient();
  const navigate = useNavigate();

  // Dispositivo vinculado ao usuário autenticado; o toque na notificação revalida o acesso ao abrir.
  useEffect(() => {
    if (!isNativeApp()) return undefined;
    const offToken = onNativeToken("apoio");
    const offOpen = onNotificationOpen((route) => navigate(route));
    (async () => {
      const status = await pushStatus();
      if (status?.granted) await enablePush("apoio").catch(() => {});
    })();
    return () => {
      offToken();
      offOpen();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (patients === null) return <Loading label="Carregando acompanhamento autorizado…" />;

  return (
    <div data-testid="apoio-layout" className="min-h-screen bg-bone">
      <header className="sticky top-0 z-40 border-b border-border bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-md items-center justify-between px-6 py-4">
          <div>
            <BrandLockup size="sm" product="Rede de apoio" testId="apoio-brand" />
            <p data-testid="apoio-user-name" className="mt-1 text-[12.5px] text-ink-500">
              {user.name}
            </p>
          </div>
          <button
            data-testid="apoio-logout"
            onClick={async () => {
              await signOut();
              navigate("/apoio/login", { replace: true });
            }}
            aria-label="Sair"
            className="rounded-full p-2 text-ink-500 transition-colors hover:bg-sand"
          >
            <LogOut size={18} />
          </button>
        </div>
      </header>

      {patients.length === 0 ? (
        <NoPatients />
      ) : !selectedPatientId ? (
        <ChoosePatient />
      ) : (
        <>
          <main className="mx-auto max-w-md px-5 pb-32 pt-6">
            <Outlet />
          </main>
          <BottomTabs />
        </>
      )}
    </div>
  );
};

export default function ApoioLayout() {
  const { user, loading } = useAuth();

  if (loading) return <Loading label="Carregando…" />;
  if (!user) return <Navigate to="/apoio/login" replace />;

  return (
    <PatientProvider>
      <ApoioShell />
    </PatientProvider>
  );
}
